import * as React from "react";
import ApiAuthenticate from "../api/authenticate";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

export interface AuthUser {
  id: number;
  organization_id: number;
  name: string;
  email: string;
  notify_order_started?: boolean;
  notify_order_complete?: boolean;
  notify_survey_locked?: boolean;
  organization_role?: "admin" | "member" | "fybr_admin";
}

export interface AuthState {
  currentUser: AuthUser | null;
  isAuthenticated: boolean;
  isPendingAuthentication: boolean;
  error: string | null;
}

type AuthAction =
  | { type: "LOGIN_SUCCESS"; user: AuthUser }
  | { type: "LOGIN_FAILURE"; error?: string }
  | { type: "LOGOUT" };

export interface AuthActions {
  login: (email: string, password: string) => Promise<boolean | string>;
  logout: () => Promise<boolean>;
  requestCode: (email: string) => Promise<boolean | string>;
  passwordReset: (
    email: string,
    resetCode: string,
    newPassword: string
  ) => Promise<boolean>;
}

const AuthStateContext = React.createContext<AuthState | undefined>(undefined);
const AuthActionsContext = React.createContext<AuthActions | undefined>(
  undefined
);

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  if (action.type === "LOGIN_SUCCESS") {
    return {
      currentUser: action.user,
      isAuthenticated: true,
      isPendingAuthentication: false,
      error: null,
    };
  }

  if (action.type === "LOGIN_FAILURE") {
    return {
      currentUser: null,
      isAuthenticated: false,
      isPendingAuthentication: false,
      error: action.error || null,
    };
  }

  if (action.type === "LOGOUT") {
    return {
      currentUser: null,
      isAuthenticated: false,
      isPendingAuthentication: false,
      error: null,
    };
  }

  throw new Error(`Auth reducer does not handle this type: ${(action as any).type}`);
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [state, dispatch] = React.useReducer(reducer, {
    currentUser: null,
    isAuthenticated: false,
    isPendingAuthentication: document.cookie.includes("fybr-cloud"),
    error: null,
  } as AuthState);

  React.useEffect(() => {
    if (!state.currentUser && state.isPendingAuthentication) {
      ApiAuthenticate.whoami().then((response) => {
        if (response.error) {
          document.cookie =
            "fybr-cloud" + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
          dispatch({ type: "LOGOUT" });
          navigate("/login");
          return;
        }
        if (response.data && response.data.user) {
          dispatch({ type: "LOGIN_SUCCESS", user: response.data.user });
        } else {
          dispatch({ type: "LOGIN_FAILURE" });
        }
      });
    }
  }, [state.currentUser, state.isPendingAuthentication]);

  const logout = useCallback(async () => {
    await ApiAuthenticate.logout();
    dispatch({ type: "LOGOUT" });
    return true;
  }, [dispatch]);

  const actions: AuthActions = {
    login: async (email: string, password: string) => {
      if (!email || !password) {
        throw new Error("This should be validated before submission");
      }

      const response = await ApiAuthenticate.login(email, password);

      if (response.error) {
        return response.error.message;
      }

      if (response.data.user) {
        dispatch({ type: "LOGIN_SUCCESS", user: response.data.user });
        return true;
      }

      return false;
    },
    logout,
    requestCode: async (email: string): Promise<boolean | string> => {
      const response = await ApiAuthenticate.requestCode(email);

      if (response.error) {
        return response.error.message;
      }

      if (response.data || response.message) {
        return true;
      }

      return false;
    },
    passwordReset: async (
      email: string,
      resetCode: string,
      newPassword: string
    ): Promise<boolean> => {
      const response = await ApiAuthenticate.resetPassword(
        email,
        resetCode,
        newPassword
      );

      if (response.error) {
        return response.error.message;
      }

      if (response.data || response.message) {
        navigate("/login");
        return true;
      } else {
        console.warn("unknown error:", response);
      }

      return false;
    },
  };

  return (
    <AuthStateContext.Provider value={state}>
      <AuthActionsContext.Provider value={actions}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuthState = (): AuthState => {
  const context = React.useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error("Must 'useAuthState' within an AuthProvider.");
  }
  return context;
};

export const useAuthActions = (): AuthActions => {
  const context = React.useContext(AuthActionsContext);
  if (context === undefined) {
    throw new Error("Must 'useAuthActions' within an AuthProvider.");
  }
  return context;
};
